import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import LogoLockup from '../../components/shared/LogoLockup';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading, error, clearError, user } = useAuth();
  
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const rolePath = user.registered === false ? '/kyc' : user.role === 'admin' ? '/admin/users' : '/dashboard';
      const from = location.state?.from?.pathname || rolePath;
      navigate(user.registered === false ? '/kyc' : from, { replace: true });
    }
  }, [isAuthenticated, user, navigate, location]);

  // Clear errors on mount
  useEffect(() => {
    clearError();
  }, [clearError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalLoading(true);
    
    const result = await login(identifier, password);
    
    if (result.success) {
      const rolePath = result.user?.registered === false ? '/kyc' : getRoleDashboardPath(result.user?.role);
      const from = location.state?.from?.pathname || rolePath;
      navigate(result.user?.registered === false ? '/kyc' : from, { replace: true });
    }
    
    setLocalLoading(false);
  };

  const getRoleDashboardPath = (role) => {
    switch (role) {
      case 'admin': return '/admin/users';
      case 'subscriber': return '/dashboard';
      case 'care_officer': return '/dashboard';
      case 'supervisor': return '/dashboard';
      case 'customer_care': return '/dashboard';
      default: return '/dashboard';
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <LogoLockup size="lg" />
          </Link>
          <p className="text-sm text-gray-500 mt-2">Sign in to your account</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm border border-red-100">
                {error}
              </div>
            )}

            {/* Login Identifier Field */}
            <div className="space-y-2">
              <Label htmlFor="identifier" className="text-gray-700">Email or phone number</Label>
              <Input
                id="identifier"
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="you@example.com or +234..."
                required
                className="h-12 rounded-xl border-gray-200 focus:border-burgundy-500 focus:ring-burgundy-500"
                data-testid="login-identifier-input"
              />
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-gray-700">Password</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-burgundy-600 hover:text-burgundy-700"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="h-12 rounded-xl border-gray-200 focus:border-burgundy-500 focus:ring-burgundy-500 pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={localLoading || isLoading}
              className="w-full h-12 rounded-xl bg-burgundy-600 hover:bg-burgundy-700 text-[#FAFAF7] font-medium"
              data-testid="login-submit-btn"
            >
              {(localLoading || isLoading) ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            Don't have an account?{' '}
            <Link to="/register" className="text-burgundy-600 hover:text-burgundy-700 font-medium">
              Register
            </Link>
          </p>
        </div>

        <p className="text-center text-xs text-gray-400 mt-8">
          Magnolia Care - Because no parent should be forgotten.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
